import WebFont from "webfontloader";
import {getDialogueMessages} from "../i18n/index.mjs";
import {initializeNeuroGuide, QuestionTypes, Radio} from "@ignitesales/neuro-application";
import dialogueReducer from "../state/DialogueReducer.mjs";
import ThemeLoadingPage from "../core-components/ThemeLoadingPage.jsx";
import deepmerge from 'deepmerge';
import logger from "../utils/logger.mjs";
import onDialogueStateInit from "../utils/onDialogueStateInit.mjs";
import {getQuestionStyles, QUESTION_STYLE_MSG_KEYS} from "../i18n/custom/question-styles/index.mjs";

const dialogueQuestionTypes = {
    ...QuestionTypes,
    xs: {
        Slider: Radio
    },
    sm: {
        Slider: Radio
    },
    md: {
        Slider: Radio
    }
};

/**
 *
 * @typedef DialogueThemeInitOptions
 * @property {Object} [customConfigs] - custom configs for theme
 * @property {Object} [htmlMessages] - html messages
 * @property {Object} [messages] - application i18n messages
 * @property {Array} [reducers] - theme reducers
 * @property {React.Component} ThemeComponent - application component
 * @property {React.Component} ThemeLoadingComponent - application is loading... component
 * @property {string} questionStylesKey - string to grab HTML messages needed for question styles
 * @property {Object} [webFont] - web font object to be loaded
 * @property {Boolean} [useDemoBuild] - builds demo assets if true
 * @property {Object} [questionTypes] - question types for respective theme, will be merged with defaults
 * @property {Object} [questionLayouts] - question group layouts for respective theme, will be merged with defaults
 * @type {DialogueThemeInitOptions}
 */
const defaultThemeOptions = {
    customConfigs: {},
    htmlMessages: {},
    messages: getDialogueMessages(true), //TODO-JJ: mock messages need to be dependent, refactor to shared
    reducers: [],
    ThemeComponent: null,
    ThemeLoadingComponent: ThemeLoadingPage,
    questionStylesKey: QUESTION_STYLE_MSG_KEYS.basic,
    webFont: null,
    useDemoBuild: false,
    questionTypes: dialogueQuestionTypes,
    questionLayouts: {}
};

/**
 * Dialogue theme initialization function
 *
 * @param {DialogueThemeInitOptions} options
 * @returns {Promise<boolean>}
 */
const initializeDialogueTheme = async (options) => {
    defaultThemeOptions.htmlMessages = getQuestionStyles(
        options.questionStylesKey || defaultThemeOptions.questionStylesKey
    );

    /**
     * @type {DialogueThemeInitOptions}
     * @private
     */
    const _opts = deepmerge( defaultThemeOptions, options );

    if(options.webFont) {
        WebFont.load(_opts.webFont);
    }

    await initializeNeuroGuide({
        AppComponent: _opts.ThemeComponent,
        AppLoadingComponent: _opts.ThemeLoadingComponent,
        messages: _opts.messages,
        reducers: [ dialogueReducer, ..._opts.reducers ],
        guideConfig: {
            customMessages: _opts.htmlMessages,
            isDialogue: true,
            customConfigs: _opts.customConfigs,
            questionTypes: _opts.questionTypes,
            questionLayouts: _opts.questionLayouts,
            isDemoMode: _opts.useDemoBuild,
            deepLinking: {
                pathMap: {
                    summary: {
                        type: 'summary'
                    }
                }
            }
        },
        onStateInit: async (neuroState) => {
            const updatedState = await onDialogueStateInit(neuroState);
            return {
                ...updatedState,
                guideConfigurations: {
                    ...updatedState.guideConfigurations,
                    deepLinking: updatedState.guideConfigurations?.deepLinking || {}
                }
            };
        }
    });

    logger.info('Dialogue theme is ready for takeoff!');
};

export default initializeDialogueTheme;
